import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import api, { API } from "@/lib/api";
import {
  Home, Building2, Map, ListChecks, ClipboardList, Wrench, Users, BarChart3, TrendingUp,
  Bell, LogOut, Menu, X, CheckSquare, Clock, CalendarDays, Briefcase, ShieldCheck, Banknote, Wallet, CalendarHeart, Palette, UserPlus, User, BadgeCheck,
  Mail, Inbox, FileText, IdCard, Shirt, CalendarRange,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { logger } from "@/lib/logger";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import UserAvatar from "@/components/app/UserAvatar";
import { Toaster } from "@/components/ui/sonner";
import EmployeeOnboardingModal from "@/components/app/EmployeeOnboardingModal";
import PendingVerificationGate from "@/components/app/PendingVerificationGate";
import TermsAcceptanceModal from "@/components/app/TermsAcceptanceModal";
import WarningsModal from "@/components/app/WarningsModal";
import WarningBanner from "@/components/app/WarningBanner";
import MoodCheckModal from "@/components/app/MoodCheckModal";
import ProbationReminderModal from "@/components/app/ProbationReminderModal";
import InstallPWA from "@/components/app/InstallPWA";
import { SidebarReorderDialog } from "@/components/app/SidebarReorderDialog";
import { AlertTriangle, ShieldAlert, Sparkles, ScrollText, Heart, ScanFace, CalendarCheck, ArrowUpDown, BookOpen } from "lucide-react";

function getNavItems() {
  return [
    { to: "/dashboard", label: "Dashboard", icon: Home, perm: "dashboard" },
    { to: "/my-tasks", label: "My Tasks", icon: CheckSquare, perm: "my_tasks" },
    { to: "/attendance", label: "Attendance", icon: Clock, perm: "attendance" },
    { to: "/my-shifts", label: "My Shifts", icon: CalendarDays, perm: "shifts" },
    { to: "/shifts", label: "Shifts", icon: CalendarRange, perm: "shifts_manage" },
    { to: "/tasks", label: "Tasks", icon: ListChecks, perm: "tasks" },
    { to: "/checklists", label: "Checklists", icon: ClipboardList, perm: "checklists" },
    { to: "/tickets", label: "Tickets", icon: Wrench, perm: "tickets" },
    { to: "/zones", label: "Zones & Areas", icon: Map, perm: "zones" },
    { to: "/properties", label: "Properties", icon: Building2, perm: "properties" },
    { to: "/staff", label: "Staff", icon: Users, perm: "staff" },
    { to: "/pending-approvals", label: "Pending Approvals", icon: UserPlus, perm: "pending_approvals" },
    { to: "/team-tenure", label: "Team Tenure", icon: BadgeCheck, perm: "team_tenure" },
    { to: "/id-cards", label: "ID Cards", icon: IdCard, perm: "id_cards" },
    { to: "/reports", label: "Reports", icon: BarChart3, perm: "reports" },
    { to: "/payroll", label: "Payroll", icon: Banknote, perm: "payroll" },
    { to: "/salary-structures", label: "Salary Structures", icon: Wallet, perm: "salary_structures" },
    { to: "/hr-dashboard", label: "HR Dashboard", icon: BarChart3, perm: "hr_dashboard" },
    { to: "/mgmt-dashboard", label: "Mgmt Dashboard", icon: TrendingUp, perm: "mgmt_dashboard" },
    { to: "/advances", label: "Advances", icon: Wallet, perm: "advances" },
    { to: "/warnings", label: "Warnings", icon: AlertTriangle, perm: "warnings" },
    { to: "/grooming-checks", label: "Grooming Checks", icon: ScanFace, perm: "grooming_review" },
    { to: "/apologies", label: "Apologies", icon: ScrollText, perm: "apologies" },
    { to: "/mood", label: "Team Mood", icon: Heart, perm: "mood_dashboard" },
    { to: "/my-payslips", label: "My Payslips", icon: Banknote, perm: "my_payslips" },
    { to: "/my-advances", label: "My Advances", icon: Wallet, perm: "my_advances" },
    { to: "/leave-balances", label: "Leave Balances", icon: CalendarHeart, perm: "leave_balances" },
    { to: "/my-leave-balance", label: "My Leave Balance", icon: CalendarHeart, perm: "my_leave_balance" },
    { to: "/my-warnings", label: "My Warnings", icon: Sparkles, perm: "my_warnings" },
    { to: "/roles", label: "Roles & Access", icon: ShieldCheck, perm: "roles" },
    { to: "/branding", label: "Branding", icon: Palette, perm: "branding" },
    { to: "/email/settings", label: "Email Settings", icon: Mail, perm: "email_settings" },
    { to: "/email/templates", label: "Email Templates", icon: FileText, perm: "email_templates" },
    { to: "/email/logs", label: "Email Logs", icon: Inbox, perm: "email_logs" },
    { to: "/uniform-categories", label: "Uniform Sizes", icon: Shirt, perm: "uniforms_manage" },
    { to: "/uniform-fulfilment", label: "Uniform Fulfilment", icon: Shirt, perm: "uniforms_manage" },
    { to: "/turnover", label: "Turnover", icon: Briefcase, perm: "turnover" },
    { to: "/bookings", label: "Bookings", icon: CalendarCheck, perm: "bookings" },
    { to: "/leave-policies", label: "Leave Policies", icon: BookOpen, always: true },
  ];
}

function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const unread = items.filter((n) => !n.read).length;

  const fetchN = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) {
      logger.error("Failed to load notifications", e);
    }
  };

  useEffect(() => {
    fetchN();
    const t = setInterval(fetchN, 30000);
    return () => clearInterval(t);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    fetchN();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 hover:bg-zinc-100 rounded-md btn-press" data-testid="notification-bell">
          <Bell className="w-5 h-5" strokeWidth={1.5} />
          {unread > 0 && (
            <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-semibold">
              {unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-auto">
        <DropdownMenuLabel className="flex justify-between items-center">
          <span>Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-zinc-500 hover:text-zinc-900" data-testid="mark-all-read">
              Mark all read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-zinc-500">No notifications</div>
        )}
        {items.map((n) => (
          <DropdownMenuItem key={n.id} className={`flex flex-col items-start gap-1 py-3 ${!n.read ? "bg-zinc-50" : ""}`}>
            <span className="text-sm">{n.message}</span>
            <span className="text-[10px] text-zinc-400">{new Date(n.created_at).toLocaleString()}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { user, logout, hasPermission } = useAuth();
  const { properties, selectedId, select } = useProperty();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [order, setOrder] = useState([]);
  const [reorderOpen, setReorderOpen] = useState(false);
  const isSuperAdmin = user?.role === "super_admin";

  const fetchOrder = React.useCallback(async () => {
    try { const { data } = await api.get("/sidebar-order"); setOrder(data.order || []); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const allItems = getNavItems();
  const orderedAll = React.useMemo(() => {
    if (!order.length) return allItems;
    const idx = {};
    order.forEach((p, i) => { idx[p] = i; });
    return [...allItems].sort((a, b) => {
      const ia = a.to in idx ? idx[a.to] : order.length + allItems.indexOf(a);
      const ib = b.to in idx ? idx[b.to] : order.length + allItems.indexOf(b);
      return ia - ib;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const items = orderedAll.filter((i) => {
    if (i.always) return true;
    if (i.roles && i.roles.length > 0) return user && i.roles.includes(user.role);
    return hasPermission(i.perm);
  });

  const isMobileRole = user?.role === "housekeeping" || user?.role === "maintenance";

  return (
    <div className="min-h-screen flex flex-col bg-zinc-50">
      {/* Top Bar */}
      <header className="glass-bar sticky top-0 z-50 border-b border-zinc-200">
        <div className="flex items-center justify-between gap-2 sm:gap-4 px-2 sm:px-4 lg:px-8 h-16">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 shrink">
            <button className="lg:hidden p-2 hover:bg-zinc-100 rounded shrink-0" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 bg-zinc-900 text-white flex items-center justify-center rounded font-display font-black text-sm shrink-0">HR</div>
              <div className="hidden md:block min-w-0">
                <div className="font-display font-bold text-sm leading-none tracking-tight truncate">Happy Retreats</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Operations</div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {properties.length > 0 && (
              <Select value={selectedId || ""} onValueChange={select}>
                <SelectTrigger className="w-[120px] sm:w-[180px] lg:w-[220px] h-9 text-xs sm:text-sm" data-testid="property-selector">
                  <SelectValue placeholder="Property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`property-option-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-1 sm:px-2 py-1 hover:bg-zinc-100 rounded shrink-0" data-testid="user-menu" aria-label="Account menu">
                  <UserAvatar user={user} size={32} />
                  <div className="hidden lg:flex flex-col items-start">
                    <span className="text-xs font-medium leading-none">{user?.name}</span>
                    <span className="text-[10px] text-zinc-500 leading-none mt-0.5">{user?.role?.replace("_", " ")}</span>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold leading-tight">{user?.name}</div>
                  <div className="text-xs font-normal text-zinc-500 mt-0.5">{user?.email}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">{user?.role?.replace("_", " ")}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="my-profile-menu">
                  <User className="w-4 h-4 mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Sidebar */}
        <aside
          className={`${mobileOpen ? "flex" : "hidden"} lg:flex flex-col fixed lg:sticky top-16 bottom-0 left-0 right-0 lg:bottom-auto lg:top-16 lg:h-[calc(100dvh-4rem)] w-full lg:w-60 bg-white border-r border-zinc-200 z-40`}
        >
          <nav className="p-3 flex flex-col gap-1 flex-1 overflow-y-auto min-h-0 overscroll-contain">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"
                  }`
                }
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <it.icon className="w-4 h-4" strokeWidth={1.5} />
                <span>{it.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="shrink-0 bg-white">
            {isSuperAdmin && (
              <button
                onClick={() => setReorderOpen(true)}
                className="mx-3 mb-1 flex items-center gap-2 px-3 py-2 rounded-md text-xs text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 transition-colors"
                data-testid="reorder-sidebar-btn"
              >
                <ArrowUpDown className="w-3.5 h-3.5" /> Reorder sidebar
              </button>
            )}
            <InstallPWA />
            <div className="px-4 py-3 border-t border-zinc-200 text-[10px] text-zinc-500 uppercase tracking-widest bg-white">
              v1.0 · Operations
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 px-4 lg:px-8 py-6 lg:py-8 max-w-full pb-24 lg:pb-8">
          <WarningBanner />
          <PendingVerificationGate>{children}</PendingVerificationGate>
        </main>
      </div>

      {/* Mobile bottom tab for staff */}
      {isMobileRole && (
        <nav className="fixed bottom-0 inset-x-0 h-16 bg-white border-t border-zinc-200 flex justify-around items-center z-50 lg:hidden shadow-[0_-4px_24px_rgba(0,0,0,0.04)]">
          {[
            { to: "/dashboard", label: "Home", icon: Home },
            { to: "/my-tasks", label: "Tasks", icon: CheckSquare },
            { to: "/tickets", label: "Tickets", icon: Wrench },
          ].map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 flex-1 h-full ${isActive ? "text-zinc-900" : "text-zinc-400"}`
              }
              data-testid={`bottom-nav-${it.label.toLowerCase()}`}
            >
              <it.icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-[10px] font-semibold uppercase tracking-wide">{it.label}</span>
            </NavLink>
          ))}
        </nav>
      )}

      <Toaster position="top-right" />
      <TermsAcceptanceModal />
      {isSuperAdmin && (
        <SidebarReorderDialog open={reorderOpen} onOpenChange={setReorderOpen} items={orderedAll} onSaved={fetchOrder} />
      )}
      <EmployeeOnboardingModal />
      <WarningsModal />
      <MoodCheckModal />
      <ProbationReminderModal />
    </div>
  );
}
